import { useEffect, useState } from "react";
import { Lock, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/api";

/**
 * Minimal, trust-oriented locked preview shown to the user before payment.
 * Exposes only:
 *  - the document title
 *  - the testator declaration opening
 *  - the first beneficiary line
 *  - asset / beneficiary counts (no values)
 * The remaining document is rendered as blurred placeholder lines + an overlay.
 */
export default function LockedPreview({ willId }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!willId) return;
    let alive = true;
    fetch(`${API}/wills/${willId}/preview`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => alive && setPreview(data))
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [willId]);

  if (error) return null; // fail silent — main PaymentGate still works
  if (!preview) {
    return (
      <Card className="border-[#0A1128]/10" data-testid="locked-preview-loading">
        <CardContent className="p-6 text-sm text-[#4A5568]">
          Preparing your preview…
        </CardContent>
      </Card>
    );
  }

  const { title, declaration_opening, first_beneficiary_line, counts, ready_message } = preview;

  return (
    <Card
      className="relative overflow-hidden border-[#0A1128]/10 shadow-[0_18px_44px_-32px_rgba(10,17,40,0.4)]"
      data-testid="locked-preview"
    >
      <CardContent className="p-0">
        <div className="border-b border-[#0A1128]/10 bg-[#FBFAF6] px-5 py-3 sm:px-7">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-gold" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-navy">
                Preview — final document locked until payment
              </span>
            </div>
            <span className="hidden text-[10px] uppercase tracking-[0.18em] text-[#4A5568] sm:inline">
              {counts?.assets ?? 0} asset{(counts?.assets ?? 0) === 1 ? "" : "s"} · {counts?.beneficiaries ?? 0} beneficiar
              {(counts?.beneficiaries ?? 0) === 1 ? "y" : "ies"}
            </span>
          </div>
        </div>

        {/* Document body — clear at top, gradually masked below */}
        <div className="relative">
          <div
            className="px-5 py-6 font-serif text-[#0A1128]/90 sm:px-10 sm:py-9"
            data-testid="locked-preview-content"
          >
            <div className="text-center">
              <div className="text-xs uppercase tracking-[0.3em] text-[#4A5568]">
                In the matter of
              </div>
              <h3 className="mt-2 font-serif text-2xl text-navy sm:text-3xl" data-testid="locked-preview-title">
                {title}
              </h3>
              <div className="mx-auto mt-3 h-px w-16 bg-gold" />
            </div>

            <p
              className="mt-6 text-[15px] leading-relaxed sm:text-base"
              data-testid="locked-preview-declaration"
            >
              {declaration_opening}
            </p>

            {first_beneficiary_line && (
              <p
                className="mt-4 text-[15px] leading-relaxed sm:text-base"
                data-testid="locked-preview-first-beneficiary"
              >
                {first_beneficiary_line}
              </p>
            )}

            {/* Blurred placeholder lines representing the rest of the document */}
            <div
              className="mt-6 space-y-3 select-none pointer-events-none"
              aria-hidden="true"
              data-testid="locked-preview-blur"
            >
              {[92, 78, 88, 64, 84, 70].map((w, i) => (
                <div
                  key={i}
                  className="h-3 rounded bg-[#0A1128]/10 blur-[3px]"
                  style={{ width: `${w}%` }}
                />
              ))}
            </div>
          </div>

          {/* Lock badge overlay */}
          <div
            className="pointer-events-none absolute bottom-5 right-5 flex items-center gap-2 rounded-full border border-navy/15 bg-white/90 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy shadow-sm backdrop-blur"
            data-testid="locked-preview-lock-badge"
          >
            <Lock size={12} /> Locked
          </div>

          {/* Fade-to-white gradient under the blur block */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
        </div>

        <div className="border-t border-[#0A1128]/10 bg-[#F9FAFB] px-5 py-3 text-center text-xs text-[#4A5568] sm:px-7">
          {ready_message}
        </div>
      </CardContent>
    </Card>
  );
}
